import React from "react";
import { Typography, Box } from "@mui/material";
import { Signal } from "../../models/signal.ts";
import {
  Communication,
  SignalMapping,
} from "../../models/electronicsConfiguration.ts";
import { Flasher } from "../../models/signalSources/flasher.ts";
import { DutTestpoint } from "../../models/signalSources/dutTestpoint.ts";
import RelaisTable from "./RelaisTable.tsx";
import VoltageTable from "./VoltageTable.tsx";
import CurrentTable from "./CurrentTable.tsx";
import ResistorTable from "./ResistorTable.tsx";
import CommunicationSignalMappingGroup, {
  CommunicationSignalMappingRow,
} from "./CommunicationSignalMappingGroup.tsx";

interface Props {
  data: SignalMapping;
  onChange: (data: SignalMapping) => void;
  communication: Communication;
  onCommunicationChange: (data: Communication, updatedSignals?: Signal[]) => void;
  flasher: Flasher;
  onFlasherChange: (data: Flasher, updatedSignals?: Signal[]) => void;
  signals: (Signal | DutTestpoint)[];
}

type CommunicationKey = "rs232" | "rs485" | "uart" | "i2c0" | "i2c1" | "usb" | "sd_card";
type StlinkKey = "stlink1" | "stlink2";

const SignalMappingForm: React.FC<Props> = ({
  data,
  onChange,
  communication,
  onCommunicationChange,
  flasher,
  onFlasherChange,
  signals,
}) => {
  const replaceSignal = (candidate: Signal, updatedSignal: Signal): Signal =>
    candidate.id === updatedSignal.id ? updatedSignal : candidate;

  const saveCommunicationGroup = (
    key: CommunicationKey,
    updatedSignals: Signal[],
  ) => {
    const currentGroup = communication[key];
    const updatedGroup = updatedSignals.reduce(
      (group, signal) => {
        const entry = Object.entries(group).find(([, value]) =>
          typeof value === "object" &&
          value !== null &&
          "id" in value &&
          (value as Signal).id === signal.id
        );
        return entry ? { ...group, [entry[0]]: replaceSignal(entry[1] as Signal, signal) } : group;
      },
      currentGroup,
    );

    onCommunicationChange({
      ...communication,
      [key]: updatedGroup,
    }, updatedSignals);
  };

  const saveStlinkGroup = (
    key: StlinkKey,
    updatedSignals: Signal[],
  ) => {
    const currentGroup = flasher[key];
    const updatedGroup = updatedSignals.reduce(
      (group, signal) => {
        const entry = Object.entries(group).find(([, value]) =>
          typeof value === "object" &&
          value !== null &&
          "id" in value &&
          (value as Signal).id === signal.id
        );
        return entry ? { ...group, [entry[0]]: replaceSignal(entry[1] as Signal, signal) } : group;
      },
      currentGroup,
    );

    onFlasherChange({
      ...flasher,
      [key]: updatedGroup,
    }, updatedSignals);
  };

  const renderGroup = (
    title: string,
    rows: CommunicationSignalMappingRow[],
    onSave: (signals: Signal[]) => void,
  ) => (
    <CommunicationSignalMappingGroup
      title={title}
      rows={rows}
      onSave={onSave}
      signalOptions={signals}
    />
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Zuordnung der Netze</Typography>
      {communication.uart.enable && renderGroup(
        "UART",
        [
          { label: "RX", signal: communication.uart.rx },
          { label: "TX", signal: communication.uart.tx },
        ],
        (updatedSignals) => saveCommunicationGroup("uart", updatedSignals),
      )}
      {communication.i2c0.enable && renderGroup(
        "I2C 0",
        [
          { label: "SDA", signal: communication.i2c0.sda },
          { label: "SCL", signal: communication.i2c0.scl },
        ],
        (updatedSignals) => saveCommunicationGroup("i2c0", updatedSignals),
      )}
      {communication.i2c1.enable && renderGroup(
        "I2C 1",
        [
          { label: "SDA", signal: communication.i2c1.sda },
          { label: "SCL", signal: communication.i2c1.scl },
        ],
        (updatedSignals) => saveCommunicationGroup("i2c1", updatedSignals),
      )}
      {communication.rs232.enable && renderGroup(
        "RS232",
        [
          { label: "RX", signal: communication.rs232.rx },
          { label: "TX", signal: communication.rs232.tx },
        ],
        (updatedSignals) => saveCommunicationGroup("rs232", updatedSignals),
      )}
      {communication.rs485.enable && renderGroup(
        "RS485",
        [
          { label: "A", signal: communication.rs485.a },
          { label: "B", signal: communication.rs485.b },
        ],
        (updatedSignals) => saveCommunicationGroup("rs485", updatedSignals),
      )}
      {communication.usb.enable && renderGroup(
        "USB2.0",
        [
          { label: "OTG", signal: communication.usb.otg },
          { label: "DP", signal: communication.usb.dp },
          { label: "DN", signal: communication.usb.dn },
        ],
        (updatedSignals) => saveCommunicationGroup("usb", updatedSignals),
      )}
      {communication.sd_card.enable && renderGroup(
        "SD-Karte",
        [
          { label: "CLK", signal: communication.sd_card.clk },
          { label: "D0", signal: communication.sd_card.d0 },
          { label: "D1", signal: communication.sd_card.d1 },
          { label: "D2", signal: communication.sd_card.d2 },
          { label: "D3", signal: communication.sd_card.d3 },
        ],
        (updatedSignals) => saveCommunicationGroup("sd_card", updatedSignals),
      )}
      {flasher.stlink1.enable && renderGroup(
        "ST-Link 1",
        [
          { label: "Target Voltage", signal: flasher.stlink1.target_voltage },
          { label: "SWDIO / TMS", signal: flasher.stlink1.swdio_tms },
          { label: "SWCLK / TCK", signal: flasher.stlink1.swclk_tck },
          { label: "SWO / TDO", signal: flasher.stlink1.swo_tdo },
          { label: "TDI", signal: flasher.stlink1.tdi },
          { label: "nTRST", signal: flasher.stlink1.ntrst },
          { label: "NRST", signal: flasher.stlink1.nrst },
        ],
        (updatedSignals) => saveStlinkGroup("stlink1", updatedSignals),
      )}
      {flasher.stlink2.enable && renderGroup(
        "ST-Link 2",
        [
          { label: "Target Voltage", signal: flasher.stlink2.target_voltage },
          { label: "SWDIO / TMS", signal: flasher.stlink2.swdio_tms },
          { label: "SWCLK / TCK", signal: flasher.stlink2.swclk_tck },
          { label: "SWO / TDO", signal: flasher.stlink2.swo_tdo },
          { label: "TDI", signal: flasher.stlink2.tdi },
          { label: "nTRST", signal: flasher.stlink2.ntrst },
          { label: "NRST", signal: flasher.stlink2.nrst },
        ],
        (updatedSignals) => saveStlinkGroup("stlink2", updatedSignals),
      )}
      <Box mt={2} />
      <Typography variant="h6" gutterBottom>Relais</Typography>
      <RelaisTable
        data={data.relais}
        onChange={(updatedRelais) => onChange({ ...data, relais: updatedRelais })}
        signalOptions={signals}
      />
      <Box mt={2} />
      <Typography variant="h6" gutterBottom>Spannungsmessungen</Typography>
      <VoltageTable
        data={data.voltage}
        onChange={(updatedVoltage) => onChange({ ...data, voltage: updatedVoltage })}
        signalOptions={signals}
      />
      <Box mt={2} />
      <Typography variant="h6" gutterBottom>Strommessungen</Typography>
      <CurrentTable
        data={data.current}
        onChange={(updatedCurrent) => onChange({ ...data, current: updatedCurrent })}
        signalOptions={signals}
      />
      <Box mt={2} />
      <Typography variant="h6" gutterBottom>Widerstände</Typography>
      <ResistorTable
        data={data.resistor}
        onChange={(updatedResistor) => onChange({ ...data, resistor: updatedResistor })}
        signalOptions={signals}
      />
    </Box>
  );
};

export default SignalMappingForm;
